import { useCallback, useEffect, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useFormik } from "formik";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  FormControlLabel,
  FormGroup,
  FormLabel,
  LinearProgress,
  TextField,
  Typography,
} from "@material-ui/core";

import useLogs from "hooks/logs";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";

export default function FormReasonDialog({
  open,
  formType,
  onClose,
  selectedReason,
}) {
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem } = useLogs();

  const [submitting, setSubmitting] = useState(false);

  const submit = async (values) => {
    const { motivo, ativo } = values;
    const updatedReason = {};

    setSubmitting(true);

    if (formType === "delete") {
      setSubmitting(true);

      const idMotivo = selectedReason?.IdMotivo;

      await Api.delete(
        "delete-reason",
        "trocasDevolucoes",
        { idMotivo: idMotivo },
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Motivo excluído com sucesso!`, {
              severity: "success",
            });
            setSubmitting(false);
            handleClose({ idMotivo });
          } else {
            snackbar.showMessage("Ocorreu um erro ao deletar o motivo.", {
              severity: "error",
            });
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);
          setLocalStorageApiProblem({
            idMenuAccess: "191",
            screen: "Motivos de Devoluções",
            problem: `Erro na chamada do endpoint delete-reason - ${error}`,
          });
        });
    }

    if (formType === "edit") {
      return await Api.put(
        "edit-reason",
        "trocasDevolucoes",
        {
          motivo: motivo,
          idMotivo: selectedReason.IdMotivo,
          status: ativo === true ? 1 : 0,
        },
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Motivo atualizado com sucesso!`, {
              severity: "success",
            });
            setSubmitting(false);
            handleClose({ ...updatedReason, ...res.Result });
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao atualizar o motivo. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);
          setLocalStorageApiProblem({
            idMenuAccess: "191",
            screen: "Motivos de Devoluções",
            problem: `Erro na chamada do endpoint edit-reason - ${error}`,
          });
        });
    }

    if (formType === "create") {
      return await Api.post(
        "create-reason",
        "trocasDevolucoes",
        { motivo: motivo },
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Motivo criado com sucesso!`, {
              severity: "success",
            });
            setSubmitting(false);
            handleClose({ ...updatedReason, ...res.Result });
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao criar o motivo. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);
          setLocalStorageApiProblem({
            idMenuAccess: "191",
            screen: "Motivos de Devoluções",
            problem: `Erro na chamada do endpoint create-reason - ${error}`,
          });
        });
    }

    return;
  };

  useEffect(() => {
    const handleActiveToggle = async () => {
      if (formType === "active" && selectedReason) {
        const ativo = selectedReason.Status;

        setSubmitting(true);

        try {
          const res = await Api.put(
            "edit-reason",
            "trocasDevolucoes",
            {
              motivo: selectedReason?.Motivo,
              idMotivo: selectedReason.IdMotivo,
              status: ativo === 0 ? 1 : 0,
            },
            true
          );

          if (res.Success) {
            snackbar.showMessage(
              `Motivo ${selectedReason.IdMotivo.toString()} foi ${
                ativo === 1 ? "desativado" : "ativado"
              } com sucesso!`,
              {
                severity: "success",
              }
            );
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao ${
                ativo === 1 ? "desativar" : "ativar"
              } Motivo ${selectedReason.IdMotivo.toString()}. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
          }
        } catch (error) {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setLocalStorageApiProblem({
            idMenuAccess: "191",
            screen: "Motivos de Devoluções",
            problem: `Erro na chamada do endpoint edit-reason - ${error}`,
          });
        } finally {
          setSubmitting(false);
          handleClose();
        }
      }
    };

    handleActiveToggle();
  }, [formType, selectedReason]);


  const formik = useFormik({
    initialValues: {
      motivo: selectedReason?.Motivo || "",
      ativo: selectedReason?.Status === 1 ? true : false,
    },
    onSubmit: submit,
  });

  const { values, handleChange, handleSubmit, dirty, resetForm } = formik;

  const handleClose = useCallback(
    (updatedReason) => {
      resetForm();
      onClose(updatedReason);
    },
    [onClose, resetForm]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        maxWidth={"xl"}
      >
        <PerfectScrollbar>
          <DialogTitle>
            <span>
              <Typography id="alert-dialog-title" variant="h3" component="h3">
                {`${
                  formType === "edit"
                    ? "Editar"
                    : formType === "delete"
                    ? "Excluir"
                    : formType === "active"
                    ? selectedReason.Status === 1
                      ? "Inativar"
                      : "Ativar"
                    : "Criar"
                } Motivo`}
              </Typography>
            </span>
          </DialogTitle>
          <PerfectScrollbar>
            <DialogContent style={{ padding: "1rem 1.5rem 0" }}>
              {formType === "delete" ? (
                <>
                  <Typography variant="h4" style={{ marginBottom: "1rem" }}>
                    Confirmar exclusão do Motivo?
                  </Typography>
                </>
              ) : formType === "active" ? (
                <>
                  <Typography variant="h4" style={{ marginBottom: "1rem" }}>
                    {`Confirmar ${
                      selectedReason.Status === 1 ? "inativação" : "ativação"
                    } do Motivo?`}
                  </Typography>
                </>
              ) : (
                <>
                  <Grid item xs={12}>
                    <FormGroup>
                      <FormLabel>Motivo *</FormLabel>
                      <TextField
                        name="motivo"
                        required
                        fullWidth
                        disabled={submitting}
                        value={values.motivo}
                        variant="outlined"
                        onChange={handleChange}
                        onInput={(e) => {
                          e.target.value = e.target.value
                            .toString()
                            .slice(0, 200);
                        }}
                      />
                    </FormGroup>
                  </Grid>
                  {formType === "edit" && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values.ativo}
                            onChange={handleChange}
                            name="ativo"
                            color="primary"
                            disabled={submitting}
                          />
                        }
                        label={<FormLabel>Ativo</FormLabel>}
                      />
                    </Grid>
                  )}
                </>
              )}
              {submitting && (
                <div style={{ marginTop: "1rem" }}>
                  <span>{`${
                    formType === "create"
                      ? "Criando"
                      : formType === "edit"
                      ? "Atualizando"
                      : formType === "active"
                      ? selectedReason.Status === 1
                        ? "Inativando"
                        : "Ativando"
                      : "Deletando"
                  } Motivo...`}</span>
                  <LinearProgress />
                </div>
              )}
            </DialogContent>
          </PerfectScrollbar>
          <DialogActions>
            <Button component={Box} onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            {formType === "delete" ? (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={submitting}
                onClick={submit}
              >
                Confirmar
              </Button>
            ) : formType === "active" ? (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={submitting}
                onClick={submit}
              >
                {selectedReason.Status == 1 ? "Inativar" : "Ativar"}
              </Button>
            ) : (
              <Button
                color="default"
                variant="contained"
                type="submit"
                disabled={
                  submitting ||
                  (formType !== "edit" && !dirty) ||
                  MaskAndValid.isNullOrEmpty(values.motivo)
                }
                onClick={handleSubmit}
              >
                {formType === "edit" ? "Atualizar" : "Criar"}
              </Button>
            )}
          </DialogActions>
        </PerfectScrollbar>
      </Dialog>
    </>
  );
}
